import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { checkIndexStatus, getIndexBuildingStatus, waitForIndexes } from '../../utils/firebaseIndexes';

const IndexStatusChecker = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const checkStatus = async () => {
    setLoading(true);
    setMessage('');
    try {
      const indexStatus = await getIndexBuildingStatus();
      setStatus(indexStatus);
      setMessage(indexStatus.message);
    } catch (error) {
      setMessage('Error checking index status');
    } finally {
      setLoading(false);
    }
  };

  const waitForIndexesToBuild = async () => {
    setLoading(true);
    setMessage('Waiting for indexes to build...');
    try {
      const result = await waitForIndexes(60000); // Wait up to 1 minute
      if (result) {
        setMessage('All indexes are now ready!');
        await checkStatus(); // Refresh status
      } else {
        setMessage('Timeout waiting for indexes to build');
      }
    } catch (error) {
      setMessage('Error waiting for indexes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  if (!status) {
    return (
      <div className="p-4 bg-white rounded-lg shadow">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Firebase Index Status
      </h3>
      
      <div className="mb-4">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
          status.allBuilt 
            ? 'bg-green-100 text-green-800' 
            : 'bg-yellow-100 text-yellow-800'
        }`}>
          {status.allBuilt ? (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>All Indexes Ready</span>
            </>
          ) : (
            <>
              <Clock className="w-4 h-4" />
              <span>Indexes Building</span>
            </>
          )}
        </div>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">{message}</p>
        </div>
      )}

      {status.details && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Question Bank Indexes:</h4>
          <div className="space-y-2">
            {Object.entries(status.details.questionBank).map(([indexName, isBuilt]) => (
              <div key={indexName} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{indexName}</span>
                <span className={`text-sm font-medium flex items-center gap-1 ${
                  isBuilt ? 'text-green-600' : 'text-red-600'
                }`}>
                  {isBuilt ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Ready</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      <span>Building</span>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex space-x-3">
        <button
          onClick={checkStatus}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Checking...' : 'Refresh Status'}
        </button>
        
        {!status.allBuilt && (
          <button
            onClick={waitForIndexesToBuild}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Waiting...' : 'Wait for Indexes'}
          </button>
        )}
      </div>

      <div className="mt-4 p-3 bg-gray-50 rounded-md">
        <p className="text-xs text-gray-600">
          <strong>Note:</strong> Index building can take several minutes, especially for large collections. 
          If indexes are still building, the application will use fallback queries until they're ready.
        </p>
      </div>
    </div>
  );
};

export default IndexStatusChecker;
